'use client';

import { ChangeEvent, MouseEvent, ReactNode, useCallback, useEffect, useRef } from 'react';

import Button from '@mui/material/Button';

import Card from '@mui/material/Card';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { tableClasses } from '@mui/material/Table';
import { tableBodyClasses } from '@mui/material/TableBody';
import { tableCellClasses } from '@mui/material/TableCell';
import { tableHeadClasses } from '@mui/material/TableHead';
import TablePagination, { tablePaginationClasses } from '@mui/material/TablePagination';

import { tableRowClasses } from '@mui/material/TableRow';

import { EmptyBox } from './emptyBox';
import { Scrollbar } from './scrollbar';
import { defaultPerPageOptions } from '../../constants/pagination';

interface BaseProps {
  cardBgColor?: string;
  children: ReactNode;
  embedded?: boolean;
  headerProps?: CardHeaderProps;
  hidePaginationWhenSingle?: boolean;
  paginationBgColor?: string;
  tableBgColor?: string;
  tableHeaderBgColor?: string;
  tableHeaderCellBgColor?: string;
}

interface PaginationProps {
  children: ReactNode;
  count: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (page: number) => void;
  page: number;
  perPage: number;
  perPageOptions?: number[];
}

type Props = BaseProps | (BaseProps & PaginationProps);

export function TableCard({
  cardBgColor = 'transparent',
  children,
  embedded,
  headerProps,
  hidePaginationWhenSingle,
  paginationBgColor,
  tableBgColor,
  tableHeaderBgColor = 'background.paper',
  tableHeaderCellBgColor = 'background.table',
  ...otherProps
}: Props) {
  const scrollbarRef = useRef<{ contentWrapperEl?: HTMLDivElement }>(null);
  const { count, onPageChange, onPerPageChange, page, perPage, perPageOptions } = (otherProps ||
    {}) as PaginationProps;

  const handlePerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = Number.parseInt(event.target.value, 10);

      if (value !== perPage) {
        onPerPageChange?.(value);
      }
    },
    [onPerPageChange, perPage],
  );

  const handlePageChange = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, page: number) => {
      onPageChange?.(page);
    },
    [onPageChange],
  );

  useEffect(() => {
    if (scrollbarRef.current?.contentWrapperEl) {
      scrollbarRef.current.contentWrapperEl.scrollTop = 0;
    }
  }, [page]);

  const paginationVisible = (!hidePaginationWhenSingle || count > perPage) && count > 0;

  const isMaximumPageExceeded = count > 0 && count <= page * perPage;

  if (isMaximumPageExceeded) {
    return (
      <EmptyBox
        action={
          onPageChange ? (
            <Button
              onClick={() => {
                onPageChange(0);
              }}
              variant="contained"
            >
              Go to the first page
            </Button>
          ) : undefined
        }
        title="The requested page has no data"
      />
    );
  }

  return (
    <Card
      sx={{
        bgcolor: cardBgColor,
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...(tableBgColor ? { [`.${tableClasses.root}`]: { bgcolor: tableBgColor } } : {}),
        [`.${tableHeadClasses.root}`]: {
          bgcolor: tableHeaderBgColor,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          [`.${tableCellClasses.root}`]: {
            bgcolor: tableHeaderCellBgColor,
            borderRadius: embedded ? 0 : undefined,
          },
        },
        [`.${tableBodyClasses.root}`]: {
          [`.${tableRowClasses.root}`]: {
            [`&:last-child`]: {
              ...(paginationVisible
                ? {
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                  }
                : {}),
              [`.${tableCellClasses.root}:first-of-type, .${tableCellClasses.root}:last-of-type`]:
                paginationVisible || embedded
                  ? {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : {},
            },
          },
        },
        [`.${tablePaginationClasses.toolbar}`]: {
          minHeight: 44,
        },
        [`.${tablePaginationClasses.selectLabel}`]: {
          marginY: 0,
        },
        [`.${tablePaginationClasses.displayedRows}`]: {
          marginY: 0,
        },
      }}
    >
      {headerProps && <CardHeader {...headerProps} />}
      <Stack maxHeight="100%" minHeight={0}>
        <Scrollbar ref={scrollbarRef}>{children}</Scrollbar>
        {paginationVisible && (
          <TablePagination
            component="div"
            count={count}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePerPageChange}
            page={page}
            rowsPerPage={perPage}
            rowsPerPageOptions={perPageOptions || defaultPerPageOptions}
            sx={{ bgcolor: paginationBgColor }}
          />
        )}
      </Stack>
    </Card>
  );
}
